import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { useTranslate } from '@tolgee/react';

interface Props {
  step: 1 | 2;
  stepLabelKey:
    | 'externalContent.step.about'
    | 'externalContent.step.content';
}

/**
 * Page-level hero shown on the public content-submission flow. Gives contributors
 * (who arrive via a shared link without any prior context) a clear answer to:
 *   - Who am I submitting to? (location name)
 *   - What is this? (one-line description)
 *   - Where am I in the flow? (Step N of 2 + current section label)
 */
const CollectHero: FC<Props> = ({ step, stepLabelKey }) => {
  const { t } = useTranslate();
  // The public content-submission flow gets its location identity from the
  // server-rendered `external_connect_cloud_data` script tag, surfaced via
  // `state.externalContent.externalContentConfig`. That's where logo_url and
  // name live for both location-scope and cloud-scope collections. Falling
  // back to locationDetails (which only loads if useParams matches) keeps the
  // hero working in the few authenticated entry points that exercise it.
  const config = useSelector(
    (state: any) => state?.externalContent?.externalContentConfig
  );
  const detailsName = useSelector(
    (state: any) => state?.locationDetails?.name
  );
  const detailsImage = useSelector(
    (state: any) => state?.locationDetails?.image
  );
  const locationName =
    config?.location?.name || config?.name || detailsName;
  const locationImage =
    config?.logo_url || config?.location?.image || detailsImage;

  const title = locationName
    ? t('externalContent.hero.title', { location: locationName })
    : t('externalContent.hero.titleFallback');

  const stepLabel = t('externalContent.step', {
    step: String(step),
    total: '2'
  });
  const sectionLabel = t(stepLabelKey);

  return (
    <div className="d-flex flex-column gap-2 mb-3 mb-md-4">
      {(locationImage || locationName) && (
        <div
          className="d-flex flex-row align-items-center gap-3 pb-3 mb-1"
          style={{ borderBottom: '1px solid rgba(15, 23, 42, 0.08)' }}
        >
          {locationImage ? (
            <img
              src={locationImage}
              alt={locationName || ''}
              style={{
                width: 44,
                height: 44,
                borderRadius: 8,
                objectFit: 'cover',
                flexShrink: 0,
                border: '1px solid rgba(15, 23, 42, 0.06)'
              }}
            />
          ) : (
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 8,
                backgroundColor: '#f1f5f9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
              aria-hidden="true"
            >
              <i
                className="fa-duotone fa-location-dot text-primary"
                style={{ fontSize: '1.2rem' }}
              ></i>
            </div>
          )}
          <div className="d-flex flex-column">
            <span
              className="text-uppercase fw-semibold"
              style={{
                fontSize: '0.7rem',
                letterSpacing: '0.06em',
                color: '#94a3b8',
                lineHeight: 1
              }}
            >
              {t('externalContent.submittingTo')}
            </span>
            <span
              className="fw-semibold"
              style={{ fontSize: '1rem', color: '#0f172a', lineHeight: 1.4 }}
            >
              {locationName || t('externalContent.thisLocation')}
            </span>
          </div>
        </div>
      )}
      <div
        className="d-flex flex-row align-items-center gap-2 text-uppercase fw-semibold"
        style={{ fontSize: '0.72rem', letterSpacing: '0.06em', color: '#64748b' }}
      >
        <span>{stepLabel}</span>
        <span aria-hidden="true">·</span>
        <span>{sectionLabel}</span>
      </div>
      <h1
        className="m-0 fw-bold"
        style={{ fontSize: '1.6rem', lineHeight: 1.2, color: '#0f172a' }}
      >
        {title}
      </h1>
      <p
        className="m-0 text-muted"
        style={{ fontSize: '0.95rem', maxWidth: '60ch' }}
      >
        {t('externalContent.hero.subtitle')}
      </p>
      <div
        className="d-flex flex-row gap-1 mt-2"
        role="progressbar"
        aria-valuenow={step}
        aria-valuemin={1}
        aria-valuemax={2}
      >
        {[1, 2].map((i) => (
          <div
            key={i}
            style={{
              height: 4,
              flex: 1,
              borderRadius: 999,
              backgroundColor: i <= step ? '#3699ff' : '#e2e8f0',
              transition: 'background-color 200ms ease-out'
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default CollectHero;
